import { useCallback, useMemo, useState } from 'react';
import { useShipperOptionsQuery } from 'src/hooks/useShipperOptions.query';

type Params = {
  /** 帳票対象の開始日 (YYYY-MM-DD) */
  startOn: string;
  /** 帳票対象の終了日 (YYYY-MM-DD) */
  endOn: string;
  /**
   * 帳票対象の shift_id 群。
   * 0 件の場合 (= 選択なし) は呼び出し側で「全件」相当の shift_id 配列を渡す前提。
   * V3Plans / ConvenienceDispatches とも仕様は同じ。
   */
  shiftIds: number[];
};

/**
 * V3Plans / ConvenienceDispatches 等で使われる帳票出力 (「出力形式を選択してください」ダイアログ) を
 * 共通化した hook。state + handlers を返す。Dialog の DOM は ReportPrintingDialogs で描画する。
 *
 * 利用例:
 *   const printing = useReportPrinting({ startOn, endOn, shiftIds });
 *   <IconButton onClick={printing.open}>...</IconButton>
 *   <ReportPrintingDialogs {...printing.dialogProps} />
 */
export const useReportPrinting = ({ startOn, endOn, shiftIds }: Params) => {
  const [selectPrintDialogOpen, setSelectPrintDialogOpen] = useState(false);
  const [directionSettingDialogOpen, setDirectionSettingDialogOpen] = useState(false);
  const [shipperDialogOpen, setShipperDialogOpen] = useState(false);
  const [shipperDialogMode, setShipperDialogMode] = useState<'delivery-by-course' | 'picking-list' | null>(null);

  const idsCsv = useMemo(() => shiftIds.join(','), [shiftIds]);

  const open = useCallback(() => setSelectPrintDialogOpen(true), []);
  const closeSelectPrint = useCallback(() => setSelectPrintDialogOpen(false), []);
  const closeDirectionSetting = useCallback(() => setDirectionSettingDialogOpen(false), []);

  const printAllOperationDirectionsOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    setDirectionSettingDialogOpen(true);
  }, []);
  const printOperationDirectionUrl = useCallback(
    () => `/operation-directions/?ids=${idsCsv}&variant=v3`,
    [idsCsv],
  );

  const printAllTruckDirectionsOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    window.open(`/truck-directions/?ids=${idsCsv}`);
  }, [idsCsv]);
  const printUnloadTruckDirectionsOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    window.open(`/truck-directions/?ids=${idsCsv}&action=降`);
  }, [idsCsv]);
  const printPickingListOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    setShipperDialogMode('picking-list');
    setShipperDialogOpen(true);
  }, []);
  const printCourseDeliveryCountReportOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    const query = new URLSearchParams({ start_on: startOn, end_on: endOn });
    window.open(`/course-delivery-counts?${query.toString()}`);
  }, [startOn, endOn]);
  const printDeliveryByCourseReportOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    setShipperDialogMode('delivery-by-course');
    setShipperDialogOpen(true);
  }, []);
  const downloadPlanCsvPerPacketOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    window.open(`/api/v3/shifts/csv?start_at_range_start_on=${startOn}&start_at_range_end_on=${endOn}&summary_type=packet`);
  }, [startOn, endOn]);
  const downloadPlanCsvPerPlaceOnClick = useCallback(() => {
    setSelectPrintDialogOpen(false);
    window.open(`/api/v3/shifts/excel?start_at_range_start_on=${startOn}&start_at_range_end_on=${endOn}&summary_type=place`);
  }, [startOn, endOn]);

  // Shipper select dialog
  const {
    data: shipperOptions,
    isLoading: shipperOptionsIsLoading,
    isError: shipperOptionsIsError,
  } = useShipperOptionsQuery({ startOn, endOn, enabled: shipperDialogOpen });
  const handleShipperDialogBack = useCallback(() => {
    setShipperDialogOpen(false);
    setShipperDialogMode(null);
    setSelectPrintDialogOpen(true);
  }, []);
  const handleShipperDialogConfirm = useCallback((selected: string[], barcodeSelected: string[] = []) => {
    setShipperDialogOpen(false);
    if (shipperDialogMode === 'picking-list') {
      const query = new URLSearchParams({ ids: idsCsv, date: startOn });
      selected.forEach((name) => query.append('shipper_names[]', name));
      barcodeSelected.forEach((name) => query.append('barcode_shipper_names[]', name));
      window.open(`/picking-list/?${query.toString()}`);
    } else {
      const query = new URLSearchParams();
      query.set('start_on', startOn);
      query.set('end_on', endOn);
      selected.forEach((name) => query.append('shipper_names[]', name));
      window.open(`/deliveries-by-course?${query.toString()}`);
    }
    setShipperDialogMode(null);
  }, [idsCsv, shipperDialogMode, startOn, endOn]);

  return {
    /** 印刷ボタン onClick: 「出力形式を選択してください」ダイアログを開く */
    open,
    /** ReportPrintingDialogs に展開して渡すための props */
    dialogProps: {
      selectPrintDialogOpen,
      onCloseSelectPrint: closeSelectPrint,
      directionSettingDialogOpen,
      onCloseDirectionSetting: closeDirectionSetting,
      printOperationDirectionUrl,
      shipperDialogOpen,
      shipperDialogMode,
      shipperOptionsNames: shipperOptions?.names ?? [],
      shipperOptionsIsLoading,
      shipperOptionsIsError,
      onShipperDialogBack: handleShipperDialogBack,
      onShipperDialogConfirm: handleShipperDialogConfirm,
      // SelectPrintPresenter に渡すハンドラ群
      printAllOperationDirectionsOnClick,
      printAllTruckDirectionsOnClick,
      printUnloadTruckDirectionsOnClick,
      printPickingListOnClick,
      printCourseDeliveryCountReportOnClick,
      printDeliveryByCourseReportOnClick,
      downloadPlanCsvPerPacketOnClick,
      downloadPlanCsvPerPlaceOnClick,
    },
  } as const;
};

export type ReportPrintingDialogProps = ReturnType<typeof useReportPrinting>['dialogProps'];
